import { Link } from "wouter";
import { motion } from "framer-motion";
import { Search, Bot, UserCog, ShieldCheck, Rocket } from "lucide-react";

const STEPS = [
  {
    num: "Step 1",
    icon: Search,
    title: "Discover Your Use Case",
    description: "We identify repetitive workflows, bottlenecks, and operational tasks that can benefit from AI automation.",
  },
  {
    num: "Step 2",
    icon: Bot,
    title: "Choose an AI Agent",
    description: "Browse our curated catalog or let our team recommend the best-fit AI solution for your business.",
    badge: "100% Money-Back Guarantee",
  },
  {
    num: "Step 3",
    icon: UserCog,
    title: "Customize & Integrate",
    description: "We tailor the AI agent to your workflows and integrate it into your existing stack.",
  },
  {
    num: "Step 4",
    icon: ShieldCheck,
    title: "Deploy & Test",
    description: "We validate workflows, optimize outputs, and ensure the system performs reliably before launch.",
  },
  {
    num: "Step 5",
    icon: Rocket,
    title: "Launch & Scale",
    description: "Expand automation across departments and scale operations with confidence.",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export function HowItWorks() {
  return (
    <motion.section
      id="how-it-works"
      className="relative w-full pt-16 pb-24 px-4 sm:px-8 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #050008 0%, #0a0418 50%, #050008 100%)",
      }}
      initial={{ opacity: 0.25 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.05 }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 30%, rgba(0,229,255,0.10) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(192,38,211,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto">

        {/* Terminal label */}
        <div className="text-center mb-3">
          <span className="terminal-label text-fuchsia-400">// TERMINAL_PROTOCOL</span>
        </div>

        {/* Section heading */}
        <div className="text-center mb-14">
          <motion.h2
            className="text-4xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif", letterSpacing: "-0.03em" }}
            initial={{ opacity: 0.2, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.8, margin: "-100px" }}
          >
            How It <span className="text-cyan-300 cyan-glow">Works</span>
          </motion.h2>
        </div>

        {/* Timeline grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6"
        >
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                variants={cardVariants}
                className="rounded-2xl p-6 flex flex-col items-center text-center transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  background: "rgba(8,6,18,0.65)",
                  border: "1px solid rgba(0,229,255,0.18)",
                  boxShadow: "0 0 0 1px rgba(192,38,211,0.06), 0 8px 32px rgba(0,0,0,0.5)",
                  backdropFilter: "blur(8px)",
                }}
              >
                {/* Number badge */}
                <span
                  className="text-2xl font-bold text-cyan-300 mb-3"
                  style={{ fontFamily: "'Spline Sans', sans-serif" }}
                >
                  {step.num}
                </span>

                {/* Round icon container */}
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto relative z-20"
                  style={{
                    background: "rgba(0,229,255,0.10)",
                    border: "1px solid rgba(0,229,255,0.35)",
                    boxShadow: "0 0 24px rgba(0,229,255,0.25)",
                  }}
                >
                  <Icon size={26} strokeWidth={2} className="text-cyan-300" />
                </div>

                {/* Title */}
                <h3
                  className="text-lg font-semibold text-white mb-1.5 leading-tight"
                  style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed text-slate-400"
                  style={{ fontFamily: "'Spline Sans', sans-serif" }}
                >
                  {step.description}
                </p>

                {/* Trust badge — only on the Choose step */}
                {step.badge && (
                  <div className="mt-4 flex justify-center">
                    <motion.div
                      animate={{ scale: [1, 1.12, 1], opacity: [1, 0.75, 1] }}
                      transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
                    >
                      <ShieldCheck size={40} strokeWidth={1.75} className="text-cyan-300" />
                    </motion.div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Link to full page */}
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link
            href="/how-it-works"
            className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            See the full process
            <span className="text-base leading-none">→</span>
          </Link>
        </motion.div>

      </div>
    </motion.section>
  );
}
